import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import config from "../../config";
import { getLanguageHeaders } from "../services/http-common";
import { formatApiError } from "../../utilities/apiErrorMessages";
import { queryClient } from "../../query/queryClient";
import { vendorQueryOptions } from "../../query/fetchers/vendor";
import { queryKeys } from "../../query/queryKeys";

const normalizeVendorPayload = (payload) => ({
    ...(payload?.data ?? {}),
    branches: payload?.data?.branches ?? [],
});

export const GetLabData = createAsyncThunk("settings/GetLabData", async (_token, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;

    try {
        const data = await queryClient.fetchQuery(vendorQueryOptions());
        return fulfillWithValue(data);
    } catch (error) {
        const message = error?.message ?? error;
        return rejectWithValue(typeof message === 'object' ? message : { message: String(message) });
    }
});

export const UpdateLabData = createAsyncThunk("settings/UpdateLabData", async (obj, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;

    try {
        const baseHeaders = {
            Accept: "application/json",
            Authorization: `Bearer ${obj.token}`,
            ...getLanguageHeaders(),
        };

        // Currency is immutable — never send it on update
        const { currency: _currency, ...values } = obj.values ?? {};

        let response;
        if (obj.logoFile) {
            const fd = new FormData();
            Object.entries(values).forEach(([key, value]) => {
                if (value === undefined || value === null) {
                    return;
                }
                if (typeof value === "boolean") {
                    fd.append(key, value ? "1" : "0");
                    return;
                }
                if (value === '' && key !== 'name') {
                    return;
                }
                fd.append(key, String(value));
            });
            if (!fd.has('name') && values?.name) {
                fd.append('name', String(values.name));
            }
            fd.append("logo", obj.logoFile);
            response = await fetch(`${config.apiUrl}`, {
                method: "POST",
                body: fd,
                headers: baseHeaders,
            });
        } else {
            response = await fetch(`${config.apiUrl}`, {
                method: "PUT",
                body: JSON.stringify(values),
                headers: {
                    ...baseHeaders,
                    "Content-Type": "application/json",
                },
            });
        }

        const data = await response.json();

        if (!response.ok) {
            return rejectWithValue(data ?? { message: 'Failed to update vendor' });
        }

        queryClient.setQueryData(queryKeys.vendor.settings(), data);

        return fulfillWithValue(data);
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const LabDataSlice = createSlice({
    name: "settings",
    initialState: {
    settings: null,
        fetchData: null,
        error: null,
        loading: false,
        updateSettings: '',
    },
    reducers: {
        syncVendorSettings: (state, action) => {
            if (!action.payload) {
                return;
            }
            state.loading = false;
            state.settings = normalizeVendorPayload(action.payload);
            state.fetchData = 'fetch';
            state.error = null;
        },
    },
    extraReducers: {

        //GetLabData actions
        [GetLabData.pending]: (state, action) => {
            state.loading = true
            state.fetchData = null
        },
        [GetLabData.fulfilled]: (state, action) => {
            state.loading = false
            state.settings = normalizeVendorPayload(action.payload)
            state.fetchData = "fetch"
        },
        [GetLabData.rejected]: (state, action) => {
            state.loading = false
            state.fetchData = null
            state.error = formatApiError(action.payload, action.error?.message ?? null)
        },
        [UpdateLabData.pending]: (state, action) => {
            state.loading = true
            state.updateSettings = ''
        },
        [UpdateLabData.fulfilled]: (state, action) => {
            state.loading = false
            state.updateSettings = 'update'
            state.settings = normalizeVendorPayload(action.payload)
        },
        [UpdateLabData.rejected]: (state, action) => {
            state.loading = false
            state.updateSettings = 'update failed'
            state.error = formatApiError(action.payload, action.error?.message ?? null)
        },
    }
})

export const { syncVendorSettings } = LabDataSlice.actions;
export default LabDataSlice.reducer;
